import {
  forwardRef,
  type InputHTMLAttributes,
  type ReactNode,
  useId,
} from "react";

export interface InputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "size"> {
  label?: string;
  error?: string;
  helperText?: string;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  /** Interactive content (e.g. password visibility). Not wrapped with pointer-events-none. */
  rightSlot?: ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      helperText,
      leftIcon,
      rightIcon,
      rightSlot,
      className = "",
      id: providedId,
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const id = providedId || generatedId;
    const hasError = Boolean(error);
    const hasRightPadding = Boolean(rightIcon || rightSlot);

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={id}
            className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1"
          >
            {label}
          </label>
        )}
        <div className="relative">
          {leftIcon && (
            <div className="absolute inset-y-0 start-0 ps-3 flex items-center pointer-events-none text-neutral-500 dark:text-neutral-400">
              {leftIcon}
            </div>
          )}
          <input
            ref={ref}
            id={id}
            className={`
              block w-full min-h-touch rounded-md
              border transition-colors duration-150
              ${leftIcon ? "ps-10" : "ps-3"}
              ${hasRightPadding ? "pe-10" : "pe-3"}
              py-2 text-base
              bg-white dark:bg-neutral-800
              text-neutral-900 dark:text-neutral-100
              placeholder:text-neutral-400 dark:placeholder:text-neutral-500
              ${
                hasError
                  ? "border-danger-500 focus:border-danger-500 focus:ring-danger-500"
                  : "border-neutral-300 dark:border-neutral-600 focus:border-primary-500 focus:ring-primary-500"
              }
              focus:outline-none focus:ring-2 focus:ring-offset-0
              disabled:bg-neutral-100 dark:disabled:bg-neutral-900
              disabled:cursor-not-allowed disabled:opacity-60
              ${className}
            `}
            aria-invalid={hasError}
            aria-describedby={
              error ? `${id}-error` : helperText ? `${id}-helper` : undefined
            }
            {...props}
          />
          {rightIcon && (
            <div className="absolute inset-y-0 end-0 pe-3 flex items-center pointer-events-none text-neutral-500 dark:text-neutral-400">
              {rightIcon}
            </div>
          )}
          {rightSlot && (
            <div className="absolute inset-y-0 end-0 flex items-center pe-2">
              {rightSlot}
            </div>
          )}
        </div>
        {error && (
          <p
            id={`${id}-error`}
            className="mt-1 text-sm text-danger-500"
            role="alert"
          >
            {error}
          </p>
        )}
        {!error && helperText && (
          <p
            id={`${id}-helper`}
            className="mt-1 text-sm text-neutral-500 dark:text-neutral-400"
          >
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = "Input";
